import type { FilterOption } from "components/shared/FiltersAutocomplete";
import { ITitle } from "containers/Titles/types";
import type { TCountries } from "countries";
import { differenceInDays } from "date-fns";
import { ITransferee } from "store/transfereesStore";
import type { ITransferTerm } from "store/transferTermsStore";

export const formatCountriesForFilter = (countries: TCountries[]) => {
    const codesForFilter = [] as { id: string; name: string }[];

    countries.forEach((country) => {
        const isCodeAlreadyThere = codesForFilter.find((item) => item.id === country.code);
        if (!isCodeAlreadyThere) {
            codesForFilter.push({ id: country.code, name: `${country.name} - ${country.code}` });
        } else {
            return;
        }
    });
    return codesForFilter;
};

/**
 * (Not used) Converts number of days into correct amount according to type -> so if you have 60 days it returns, {number: 2, type: "months"}
 * Created it in earlier version. Keeping it in case it's useful.
 */
export const formatFrequence = (termFrequence: number) => {
    if (termFrequence >= 30) {
        return { number: Math.abs(Math.round(termFrequence / 30)), type: "months" };
    } else if (termFrequence >= 365) {
        return { number: Math.abs(Math.round(termFrequence / 365)), type: "years" };
    } else {
        return { number: termFrequence, type: "days" };
    }
};

/**
 * Checks if countries changed when editing TransferTerm form.
 * @param countries New countries chosen.
 * @param oldCodes Data to compare it with.
 */
export const checkHaveCountriesChanged = (countries: FilterOption[], oldCodes: string[] | undefined) => {
    const newCodes = countries.map((country) => country.id);
    return JSON.stringify(newCodes) !== JSON.stringify(oldCodes);
};

/**
 * Needed on submit. If date is timestamp, return as is. If not, convert to timestamp.
 */
export const formatTimestampDates = (date: number | Date) => {
    if (typeof date === "number") {
        return date;
    } else if (typeof date === "string") {
        return new Date(date).getTime();
    } else {
        return date.getTime();
    }
};

/**
 * Returns the number of months between two dates
 *
 * @param start {Date} - The start date
 * @param end {Date} - The end date
 *
 * @returns {number} - The number of months between the two dates
 */
export const getMonthsBetweenDates = (startDate: Date, endDate: Date): number => {
    const [start, end] = [startDate, endDate];

    let months = (end.getFullYear() - start.getFullYear()) * 12;

    months += end.getMonth() - start.getMonth();

    // Adjust for partial months
    if (end.getDate() < start.getDate()) {
        months--;
    }

    return months;
};

/**
 * Returns payment closest to current date when creating transferTerm.
 * @param dateOptions All payment options available.
 * @param comparisonDate Current date.
 * @returns
 */
export const getClosestDate = (dateOptions: { value: number; label: string }[], comparisonDate: Date) => {
    let latestDifference = 100000000000;
    let correctOption;
    dateOptions.forEach((option) => {
        const diff = differenceInDays(new Date(option.value), comparisonDate);
        if (latestDifference > Math.abs(diff)) {
            latestDifference = Math.abs(diff);
            correctOption = option;
        }
    });

    return correctOption;
};

export const transferWorkLabel = (item: { workName?: string | null; titleName?: string | null }) =>
    item.workName || item.titleName || "";

export const transferWorkPath = (item: { workId?: number | null; titleId?: number | null }) => {
    if (item.workId) return `/works/${item.workId}`;
    if (item.titleId) return `/titles/${item.titleId}`;
    return null;
};

export const checkSimilarTermExists = (
    chosenCountries: FilterOption[],
    allTransferTerms: ITransferTerm[],
    chosenWorkId: number | null | undefined,
    chosenTransfereeId: number,
    from: "form" | "table",
) => {
    const compareCountries = (chosenCountries: FilterOption[], termCountries: string[]) => {
        const formattedChosenCountries = from === "form" ? chosenCountries.map((item) => item.id) : chosenCountries;
        return JSON.stringify(formattedChosenCountries) === JSON.stringify(termCountries);
    };

    const similarTerm = allTransferTerms.find(
        (item) =>
            chosenWorkId != null &&
            item.workId === chosenWorkId &&
            item.transfereeId === chosenTransfereeId &&
            compareCountries(chosenCountries, item.countryCodes),
    );

    return !!similarTerm;
};
